import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { hoodieFetchSessionJson } from "../../../../api/hoodieClient";
import { useClassicSiteAuth } from "../../auth";
import {
  BILLING_CAPABILITIES,
  CAPABILITY_LABELS,
  DIAGNOSTIC_GET_ENDPOINTS,
  MEMBER_EVENT_CAPABILITIES,
  MEMBER_LISTING_CAPABILITIES,
  MEMBER_PROMO_CAPABILITIES,
} from "../../api/classicSiteCapabilityAudit";
import { OWNED_LISTINGS_VERIFICATION } from "../../api/classicSiteMemberApi";
import { CLASSIC_SITE_BASE } from "../../classicSite.constants";

function CapabilityTable({ title, rows }) {
  return (
    <section className="cs-diag-section">
      <h2>{title}</h2>
      <table className="cs-table cs-diag-table">
        <thead>
          <tr>
            <th>Capability</th>
            <th>Classification</th>
            <th>API</th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.capability}>
              <td>{row.capability}</td>
              <td>
                <span className={`cs-badge cs-badge--${row.classification}`}>
                  {CAPABILITY_LABELS[row.classification] || row.classification}
                </span>
              </td>
              <td className="cs-diag-table__api">{row.api || row.django || "—"}</td>
              <td>{row.notes}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function GetProbePanel() {
  const [probes, setProbes] = useState({});
  const { isAuthenticated } = useClassicSiteAuth();

  const runProbe = useCallback(async (path, label) => {
    setProbes((prev) => ({ ...prev, [path]: { loading: true } }));
    try {
      const { data, response, ok } = await hoodieFetchSessionJson(path);
      setProbes((prev) => ({
        ...prev,
        [path]: {
          loading: false,
          ok,
          status: response.status,
          summary: ok
            ? Array.isArray(data)
              ? `${data.length} items`
              : data?.results
                ? `${data.results.length} of ${data.count} results`
                : data?.user?.email || data?.id
                  ? "OK"
                  : "OK"
            : "Failed",
        },
      }));
    } catch (err) {
      setProbes((prev) => ({
        ...prev,
        [path]: { loading: false, ok: false, status: err.status, summary: err.message },
      }));
    }
  }, []);

  return (
    <section className="cs-diag-section">
      <h2>Safe GET probes (read-only)</h2>
      <p className="cs-diag-note">
        Session: {isAuthenticated ? "authenticated" : "not authenticated"}. No
        POST/PATCH/DELETE. Results are diagnostic only.
      </p>
      <ul className="cs-diag-probes">
        {DIAGNOSTIC_GET_ENDPOINTS.map(({ path, label, requiresSession }) => (
          <li key={path} className="cs-diag-probe">
            <div>
              <strong>{label}</strong>
              <code> GET {path}</code>
              {requiresSession ? " (session)" : ""}
            </div>
            <button
              type="button"
              className="cs-btn cs-btn--ghost"
              onClick={() => runProbe(path, label)}
            >
              Probe
            </button>
            {probes[path]?.loading ? <span> …</span> : null}
            {probes[path] && !probes[path].loading ? (
              <span className={probes[path].ok ? "cs-diag-ok" : "cs-diag-fail"}>
                {" "}
                {probes[path].status} — {probes[path].summary}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function ApiCapabilitiesPage() {
  const { status, identity } = useClassicSiteAuth();

  return (
    <div className="cs-diagnostics">
      <p>
        <Link to={CLASSIC_SITE_BASE}>← Classic Site home</Link>
      </p>
      <h1>API capability audit (Phase 4)</h1>
      <p className="cs-diag-note">
        Static conclusions from Django source review. Developer-only. Not for
        production member flows.
      </p>

      <section className="cs-diag-section cs-diag-summary">
        <h2>Session</h2>
        <p>
          Status: <code>{status}</code>
          {identity ? ` — ${identity.displayName}` : ""}
        </p>
        <p>
          Owned listings: <strong>{OWNED_LISTINGS_VERIFICATION.status}</strong>{" "}
          (no owner-scoped API)
        </p>
      </section>

      <CapabilityTable title="Member listings" rows={MEMBER_LISTING_CAPABILITIES} />
      <CapabilityTable title="Member events" rows={MEMBER_EVENT_CAPABILITIES} />
      <CapabilityTable title="Member promos" rows={MEMBER_PROMO_CAPABILITIES} />
      <CapabilityTable title="Billing / account" rows={BILLING_CAPABILITIES} />

      <GetProbePanel />

      <p className="cs-diag-note">
        Full audit:{" "}
        <code>project-ops/specs/classic-site-phase-4-api-capability-audit-v1.md</code>
        {" · "}
        <Link to={`${CLASSIC_SITE_BASE}/diagnostics/staging-verification`}>
          Staging verification harness (Phase 5)
        </Link>
      </p>
    </div>
  );
}
